/**
 * Up-relative vector math for the movement abilities (planet gravity): "vertical" is the component along
 * the player's up, "horizontal" the part on the plane perpendicular to it. Every helper takes the up
 * vector and, when it is exactly (0,1,0) (no gravity field, or the field folded back), runs the literal
 * x/z/y expressions the abilities used before the field existed, so flat worlds stay bit-identical.
 * Pure and allocation-free; imports only three.
 */
import type { Vector3 } from 'three';

/** True when `up` is exactly (0,1,0): the world-up branch, the old expressions. */
export function isWorldUp(up: Vector3): boolean {
  return up.x === 0 && up.y === 1 && up.z === 0;
}

/** The component of `v` along `up` (`v.y` at world up). */
export function verticalOf(v: Vector3, up: Vector3): number {
  return isWorldUp(up) ? v.y : v.x * up.x + v.y * up.y + v.z * up.z;
}

/** Set the component of `v` along `up` to `s`, keeping the part on the tangent plane. Returns `v`. */
export function setVerticalOf(v: Vector3, up: Vector3, s: number): Vector3 {
  if (isWorldUp(up)) {
    v.y = s;
    return v;
  }
  return v.addScaledVector(up, s - verticalOf(v, up));
}

/** `out` = `v` without its component along `up` (`out` may be `v`). Returns `out`. */
export function tangentOf(v: Vector3, up: Vector3, out: Vector3): Vector3 {
  if (isWorldUp(up)) {
    out.copy(v);
    out.y = 0;
    return out;
  }
  const d = verticalOf(v, up);
  return out.set(v.x - up.x * d, v.y - up.y * d, v.z - up.z * d);
}

/** Length of the tangent part of `v` (`hypot(v.x, v.z)` at world up). */
export function tangentLength(v: Vector3, up: Vector3): number {
  if (isWorldUp(up)) return Math.hypot(v.x, v.z);
  const d = verticalOf(v, up);
  return Math.sqrt(Math.max(0, v.lengthSq() - d * d));
}

/** Dot product of the tangent parts of `a` and `b` (`a.x*b.x + a.z*b.z` at world up). */
export function tangentDot(a: Vector3, b: Vector3, up: Vector3): number {
  if (isWorldUp(up)) return a.x * b.x + a.z * b.z;
  return a.dot(b) - verticalOf(a, up) * verticalOf(b, up);
}

/** Cap the tangent part of `v` to `max` (the vertical part is kept). Returns `v`. */
export function capTangent(v: Vector3, up: Vector3, max: number): Vector3 {
  if (isWorldUp(up)) {
    const h = Math.hypot(v.x, v.z);
    if (h > max) {
      const k = max / h;
      v.x *= k;
      v.z *= k;
    }
    return v;
  }
  const h = tangentLength(v, up);
  if (h > max) {
    const d = verticalOf(v, up);
    // Scale only the tangent part: v = up*d + tan*k.
    const k = max / h;
    v.multiplyScalar(k).addScaledVector(up, d * (1 - k));
  }
  return v;
}
